import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface GratIncomeCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  incomePayments?: EstateWaterfall_TransferLogItemFragment[];
  fundingTransfer?: EstateWaterfall_TransferLogItemFragment;
}

export function createGratIncomeCard({
  sourceEntityName,
  targetEntityName,
  incomePayments,
  fundingTransfer,
}: GratIncomeCardParams): CardConfig {
  const gratName = sourceEntityName;
  const grantor = targetEntityName;

  const isHypothetical = fundingTransfer?.isHypothetical;

  let fundingAmount = (() => {
    if (fundingTransfer) {
      return fundingTransfer.finalTransferAmount;
    }

    return null;
  })();

  // Calculate total payments
  const totalPayments =
    incomePayments?.reduce((sum, payment) => {
      return sum.plus(payment.finalTransferAmount || 0);
    }, new Decimal(0)) || new Decimal(0);

  const gratTerm = (() => {
    const grat = incomePayments?.[0]?.associatedObject;
    if (!grat) {
      return null;
    }

    if (grat.__typename === 'HypotheticalGRAT') {
      return grat.simulatedGrat.gratTrust?.termDurationYears;
    }

    if (
      grat.__typename === 'Entity' &&
      grat.subtype.__typename === 'GRATTrust'
    ) {
      fundingAmount = grat.subtype.initialFundingValue;
      return grat.subtype.termDurationYears;
    }

    return null;
  })();

  const termEndDate = (() => {
    if (gratTerm) {
      return new Date().getFullYear() + gratTerm;
    }

    return null;
  })();

  return {
    element: (
      <EdgeLabelCard
        key={`grat-income-${sourceEntityName}-${targetEntityName}`}
        title={
          isHypothetical ? 'Hypothetical GRAT annuities' : 'GRAT annuities'
        }
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`${grantor} receives annuity payments from ${gratName}.`}
        rows={compact([
          fundingAmount
            ? {
                label: 'Funding amount',
                value: formatCurrencyNoDecimals(fundingAmount),
              }
            : null,
          {
            label: 'Total annuities',
            value: formatCurrencyNoDecimals(totalPayments),
          },
          gratTerm
            ? {
                label: 'GRAT term',
                value: `${gratTerm} years`,
              }
            : null,
          termEndDate
            ? {
                label: 'Term end',
                value: termEndDate.toString(),
              }
            : null,
        ])}
      />
    ),
    label: 'GRAT annuities',
    value: formatCurrencyNoDecimals(totalPayments),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
